import confetti from 'canvas-confetti';
import { useEffect, useRef } from 'react';

type ConfettiProps = {
  active: boolean;
  durationMs?: number;
  colors?: string[];
};

const DEFAULT_PALETTE = [
  '#ff3b30', // red
  '#7c3aed', // purple
  '#2563eb', // blue
  '#22c55e', // green
  '#facc15', // yellow
];

function readPalette(): string[] {
  return DEFAULT_PALETTE;
}

function prefersReducedMotion(): boolean {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export const Confetti = ({ active, durationMs = 3000, colors }: ConfettiProps): null => {
  const firedRef = useRef(false);
  const instanceRef = useRef<confetti.CreateTypes | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!active) {
      firedRef.current = false;

      return;
    }

    if (firedRef.current) return;

    firedRef.current = true;

    if (prefersReducedMotion()) return;

    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '2147483647';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    canvasRef.current = canvas;

    const fire = confetti.create(canvas, {
      resize: true,
      useWorker: false,
    });
    instanceRef.current = fire;

    const palette = colors && colors.length > 0 ? colors : readPalette();
    const paletteOrUndefined = palette.length > 0 ? palette : undefined;

    const shootBurst = (originX: number): void => {
      const origin = { x: originX, y: 0.5 };
      const base = {
        origin,
        ticks: 260,
        gravity: 1.15,
        drift: 0,
        colors: paletteOrUndefined,
      };

      void fire({
        ...base,
        particleCount: 130,
        spread: 70,
        startVelocity: 55,
        decay: 0.9,
        scalar: 1,
      });

      void fire({
        ...base,
        particleCount: 60,
        spread: 110,
        startVelocity: 45,
        decay: 0.92,
        scalar: 0.9,
      });

      void fire({
        ...base,
        particleCount: 40,
        spread: 140,
        startVelocity: 35,
        decay: 0.94,
        scalar: 0.8,
      });
    };

    const shootSideCannon = (side: 'left' | 'right'): void => {
      void fire({
        particleCount: 80,
        angle: side === 'left' ? 60 : 120,
        spread: 75,
        startVelocity: 60,
        origin: { x: side === 'left' ? 0 : 1, y: 0.8 },
        ticks: 280,
        gravity: 1.1,
        decay: 0.92,
        scalar: 0.95,
        colors: paletteOrUndefined,
      });
    };

    shootBurst(0.5);
    shootSideCannon('left');
    shootSideCannon('right');

    const timers: number[] = [];
    timers.push(
      window.setTimeout(() => shootBurst(0.25), 350),
      window.setTimeout(() => shootBurst(0.75), 650),
      window.setTimeout(() => shootSideCannon('left'), 900),
      window.setTimeout(() => shootSideCannon('right'), 1100),
    );

    const cleanupTimer = window.setTimeout(() => {
      fire.reset();
      instanceRef.current = null;

      if (canvasRef.current?.parentNode) {
        canvasRef.current.parentNode.removeChild(canvasRef.current);
      }

      canvasRef.current = null;
    }, durationMs + 1200);

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(cleanupTimer);
      instanceRef.current?.reset();
      instanceRef.current = null;

      if (canvasRef.current?.parentNode) {
        canvasRef.current.parentNode.removeChild(canvasRef.current);
      }

      canvasRef.current = null;
    };
  }, [active, durationMs, colors]);

  return null;
};
